import { useCompositionTheme } from "../design/CompositionThemeContext.js";
import { readCompositionAppearance } from "../../../util/src/design/catalogue-runtime.js";
import { CompositionDesignSurface } from "../design/CompositionDesignSurface.js";
import { customDisciplineLabel } from "../../../util/src/custom-disciplines/labels.js";
import React, { useId, useMemo } from "react";
import { Tooltip } from "@mui/material";
import type { RenderTooltip } from "../../../util/src/layout-compiler/types.js";
import { CompiledLayoutRenderer } from "./CompiledLayoutRenderer.js";
import { useCompiledLayoutRuntimeData } from "./CompiledLayoutRuntimeContext.js";

type RenderNodeTooltipShellProps = {
  tooltip?: RenderTooltip;
  ownerNodeKey: string;
  children: React.ReactElement;
};

const DEFAULT_LAYOUT_TOOLTIP_BACKGROUND = "rgba(97, 97, 97, 0.92)";

function LayoutTooltipContent({
  tooltip,
  ownerNodeKey,
}: {
  tooltip: Extract<RenderTooltip, { kind: "layout" }>;
  ownerNodeKey: string;
}) {
  const runtimeData = useCompiledLayoutRuntimeData();
  const theme = useCompositionTheme();
  const appearance = theme
    ? readCompositionAppearance(
        tooltip.layout,
        theme.mode,
        theme.color,
        runtimeData?.__customDisciplineLabels,
      )
    : undefined;
  const tooltipData = useMemo(
    () =>
      runtimeData && tooltip.rowContext
        ? { ...runtimeData, ...tooltip.rowContext }
        : runtimeData,
    [runtimeData, tooltip.rowContext],
  );
  const tooltipInstanceId = useId();
  const tooltipDocument = useMemo(
    () => ({
      ...tooltip.layout,
      scene: {
        ...tooltip.layout.scene,
        backgroundColor: "transparent",
      },
    }),
    [tooltip.layout],
  );

  const root = tooltip.layout.scene.children[0];
  const fitContentHeight =
    tooltip.layout.scene.children.length === 1 &&
    root?.kind === "container" &&
    root.layout === "flex" &&
    root.flexHeightMode === "fitContent" &&
    root.style?.position === "relative";

  if (!tooltipData) {
    return null;
  }

  return (
    <div
      style={{
        width:
          appearance?.patch.canvas?.width ?? tooltip.layout.meta.canvas.width,
        height: fitContentHeight
          ? "auto"
          : (appearance?.patch.canvas?.height ??
            tooltip.layout.meta.canvas.height),
        position: "relative",
        overflow: "hidden",
      }}
    >
      <CompositionDesignSurface
        active={!!appearance}
        color={theme?.color ?? "light"}
      >
        <CompiledLayoutRenderer
          document={tooltipDocument}
          catalogueTheme={theme}
          canvasStyle={{ background: "transparent" }}
          data={tooltipData}
          scale={1}
          isEditor={false}
          fitContentHeight={fitContentHeight}
          nodeInstanceKeyPrefix={`tooltip:${ownerNodeKey}:${tooltipInstanceId}:`}
        />
      </CompositionDesignSurface>
    </div>
  );
}

/**
 * Wraps a compiled layout node with an optional hover tooltip (text or nested layout).
 */
export function RenderNodeTooltipShell({
  tooltip,
  ownerNodeKey,
  children,
}: RenderNodeTooltipShellProps) {
  const labels = useCompiledLayoutRuntimeData()?.__customDisciplineLabels;
  const theme = useCompositionTheme();
  const appearance =
    tooltip?.kind === "layout" && theme
      ? readCompositionAppearance(
          tooltip.layout,
          theme.mode,
          theme.color,
          labels,
        )
      : undefined;
  if (!tooltip) {
    return children;
  }

  if (tooltip.kind === "text") {
    return (
      <Tooltip
        title={customDisciplineLabel(labels, tooltip.text)}
        arrow
        slotProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
      >
        {children}
      </Tooltip>
    );
  }

  return (
    <Tooltip
      arrow
      title={
        <LayoutTooltipContent tooltip={tooltip} ownerNodeKey={ownerNodeKey} />
      }
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: "none",
            p: 0,
            bgcolor:
              appearance?.patch.backgroundColor ??
              tooltip.backgroundColor ??
              DEFAULT_LAYOUT_TOOLTIP_BACKGROUND,
            color: "#fff",
            opacity:
              typeof tooltip.opacity === "number" ? tooltip.opacity : undefined,
            boxShadow: 3,
            overflow: "hidden",
          },
        },
        arrow: {
          sx: {
            color:
              appearance?.patch.backgroundColor ??
              tooltip.backgroundColor ??
              DEFAULT_LAYOUT_TOOLTIP_BACKGROUND,
            opacity:
              typeof tooltip.opacity === "number" ? tooltip.opacity : undefined,
          },
        },
      }}
    >
      {children}
    </Tooltip>
  );
}
